import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMnemonic } from '@/hooks/useMnemonic';
import { MnemonicLabel } from '@/components/mnemonics/MnemonicLabel';

/**
 * Sidebar link carrying a mnemonic. Split out of Layout because the mnemonic
 * hook cannot be called from inside the nav .map().
 *
 * When collapsed the sidebar shows icons only, so there is no label to
 * underline — the binding stays live and the letter moves into the tooltip.
 *
 * The label truncates rather than wraps: the sidebar is resizable down to
 * 200px, and "External Processing" wrapping to two lines breaks the row rhythm
 * the icons establish. The tooltip carries the full label at every width.
 */
export default function NavLinkWithMnemonic({
  to,
  mnemonic,
  label,
  icon: Icon,
  collapsed,
  isActive,
  onNavigate,
  ...props
}) {
  const ref = useRef(null);
  const active = useMnemonic(mnemonic, () => ref.current?.click(), { label: `nav: ${label}` });

  return (
    <Link
      ref={ref}
      to={to}
      className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
      onClick={onNavigate}
      title={active ? `${label} (Alt+${String(mnemonic).toUpperCase()})` : label}
      aria-keyshortcuts={active ? `Alt+${String(mnemonic).toUpperCase()}` : undefined}
      aria-label={active && !collapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      {...props}
    >
      {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
      {!collapsed && (
        <span className="truncate">{active ? <MnemonicLabel char={mnemonic}>{label}</MnemonicLabel> : label}</span>
      )}
    </Link>
  );
}
